import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export interface GuestSwipe {
  apartmentId: string;
  action: 'like' | 'pass';
  timestamp: number;
}

const STORAGE_KEY = 'guest_swipes';

/**
 * Get all guest swipes from localStorage
 */
export function getGuestSwipes(): GuestSwipe[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save a guest swipe to localStorage
 */
export function saveGuestSwipe(apartmentId: string, action: 'like' | 'pass'): void {
  try {
    const swipes = getGuestSwipes();
    
    // Check if this apartment was already swiped
    const existingIndex = swipes.findIndex(s => s.apartmentId === apartmentId);
    
    if (existingIndex >= 0) {
      // Update existing swipe
      swipes[existingIndex] = { apartmentId, action, timestamp: Date.now() };
    } else {
      // Add new swipe
      swipes.push({ apartmentId, action, timestamp: Date.now() });
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(swipes));
  } catch (error) {
    console.error('Error saving guest swipe:', error);
  }
}

/**
 * Clear all guest swipes from localStorage
 */
export function clearGuestSwipes(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing guest swipes:', error);
  }
}

/**
 * Sync all guest swipes to the backend after login
 * - Likes go to the matches table
 * - Passes go to the apartment_swipes table
 */
export async function syncGuestSwipesToBackend(userId: string): Promise<{ likesCount: number; passesCount: number }> {
  const swipes = getGuestSwipes();
  
  if (swipes.length === 0) {
    return { likesCount: 0, passesCount: 0 };
  }

  const likes = swipes.filter(s => s.action === 'like');
  const passes = swipes.filter(s => s.action === 'pass');
  
  let likesCount = 0;
  let passesCount = 0;

  try {
    // Sync likes to matches table
    if (likes.length > 0) {
      const matchInserts = likes.map(swipe => ({
        renter_id: userId,
        apartment_id: swipe.apartmentId,
      }));

      const { error: matchError } = await supabase
        .from('matches')
        .upsert(matchInserts, { 
          onConflict: 'renter_id,apartment_id',
          ignoreDuplicates: true 
        });

      if (matchError) {
        console.error('Error syncing guest likes:', matchError);
      } else {
        likesCount = likes.length;
      }
    }

    // Sync all swipes (both likes and passes) to apartment_swipes table for tracking
    if (swipes.length > 0) {
      const swipeInserts = swipes.map(swipe => ({
        renter_id: userId,
        apartment_id: swipe.apartmentId,
        action: swipe.action,
      }));

      const { error: swipeError } = await supabase
        .from('apartment_swipes')
        .upsert(swipeInserts, { 
          onConflict: 'renter_id,apartment_id',
          ignoreDuplicates: true 
        });

      if (swipeError) {
        console.error('Error syncing guest swipes:', swipeError);
      } else {
        passesCount = passes.length;
      }
    }

    // Clear localStorage after successful sync
    clearGuestSwipes();
    
    // Show toast if likes were synced
    if (likesCount > 0) {
      toast.success(`${likesCount} liked apartment${likesCount > 1 ? 's' : ''} saved to your favorites!`);
    }

    return { likesCount, passesCount };
  } catch (error) {
    console.error('Error syncing guest swipes to backend:', error);
    return { likesCount: 0, passesCount: 0 };
  }
}

/**
 * Hook for using guest swipes functionality
 * Returns functions for saving, syncing, and clearing guest swipes
 */
export function useGuestSwipesSync() {
  return {
    saveGuestSwipe,
    syncToBackend: syncGuestSwipesToBackend,
    clearGuestSwipes,
    getGuestSwipes,
  };
}

export default useGuestSwipesSync;

